import { type FetchLike, type Middleware, type WretchOptions } from 'wretch';

/**
 * This middleware adds a timeout to all wretch fetch requests using the AbortSignal.
 *
 * IMPORTANT: all wretch requests have to pass the signal: `option({ signal })`, so that the timeout can be applied to them. Otherwise, the timeout won't work.
 */
export const timeoutMiddleware: Middleware =
  ({ timeoutMs }: { timeoutMs?: number } = {}) =>
  (next: FetchLike) =>
  (url: string, opts: WretchOptions) =>
    next(url, opts.signal ? { ...opts, signal: signalWithTimeout(opts.signal, timeoutMs) } : opts);

/**
 * The signal will abort when a query is cancelled {@link https://tanstack.com/query/latest/docs/framework/react/guides/query-cancellation#default-behavior}
 * or 30 seconds {@link https://github.com/qdrant/qdrant-cloud/blob/main/environments/production/apps/qdrant-cloud-cluster-api/agent-cluster-api-release.yaml#L36} are up,
 * whichever is sooner. It shall induce a retry earlier than a network/server timeout,
 * and therefore increase the chances of a successful response in a quasi-reasonable amount of time.
 */
function signalWithTimeout(signal: AbortSignal, timeoutMs = 30_000) {
  if ('any' in AbortSignal) {
    return AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]);
  }
  return signal;
}
